import { FiEdit, FiTrash2, FiPackage, FiChevronDown, FiChevronUp, FiAlertTriangle } from 'react-icons/fi';
import { useState } from 'react';

const ProductItem = ({ product, onEdit, onDelete, onReorder, currency = {
  symbol: '₹',
  code: 'INR',
  locale: 'en-IN'
} }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const isLowStock = product.is_low_stock;
  const isExpiringSoon = product.is_expiring_soon;
  const isExpired = product.is_expired;
  
  const today = new Date();
  const expiryDate = product.expiry_date ? new Date(product.expiry_date) : null;
  const daysUntilExpiry = expiryDate ? Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24)) : null;
  
  const isWithinAlertPeriod = expiryDate && product.permissible_expiry_days && 
                          daysUntilExpiry > 0 && 
                          daysUntilExpiry <= product.permissible_expiry_days;

  return (
    <div className="w-full sm:w-[calc(50%-0.5rem)] mx-1 mb-4">
      <div className={`bg-white p-3 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow h-full 
        ${isLowStock ? 'border-l-4 border-l-orange-500' : ''}
        ${isExpiringSoon ? 'border-l-4 border-l-yellow-500' : ''}
        ${isExpired ? 'border-l-4 border-l-red-500' : ''}`}>
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-16 h-16">
            {product.image_url ? (
              <img 
                src={product.image_url}
                alt={product.name}
                className="h-full w-full object-cover rounded-md border border-gray-200"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.parentElement.innerHTML = (
                    <div className="h-full w-full flex items-center justify-center bg-gray-100 rounded-md border border-gray-200">
                      <FiPackage size={24} className="text-gray-400" />
                    </div>
                  );
                }}
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center bg-gray-100 rounded-md border border-gray-200">
                <FiPackage size={24} className="text-gray-400" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-800 truncate text-sm">{product.name}</h3>
                  {(isLowStock || isExpiringSoon || isExpired) && (
                    <FiAlertTriangle className="text-yellow-500 flex-shrink-0" size={16} />
                  )}
                </div>
                <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                  {product.category}
                </span>
              </div>
              <div className="flex gap-1 items-center flex-shrink-0">
                <button 
                  onClick={onEdit}
                  className="text-gray-500 hover:text-blue-600 p-1 rounded-md transition-colors"
                  aria-label="Edit"
                  title="Edit"
                >
                  <FiEdit size={16} />
                </button>
                {onReorder && (
                  <button
                    onClick={onReorder}
                    className="text-xs bg-green-50 text-green-700 hover:bg-green-100 px-2 py-1 rounded-md transition-colors flex items-center gap-1"
                    aria-label="Reorder"
                    title="Reorder"
                  >
                    Reorder
                  </button>
                )}
                <button 
                  onClick={onDelete}
                  className="text-gray-500 hover:text-red-600 p-1 rounded-md transition-colors"
                  aria-label="Delete"
                  title="Delete"
                >
                  <FiTrash2 size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-md transition-colors"
                  aria-label={isExpanded ? "Collapse" : "Expand"}
                >
                  {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                </button>
              </div>
            </div>

            <div className="mt-2 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <div>
                  <p className="text-gray-500">Qty</p>
                  <p className={`font-medium ${isLowStock ? 'text-red-600' : 'text-gray-800'}`}>
                    {product.current_quantity} {product.unit}
                  </p>
                </div>
                {isLowStock && (
                  <span className="self-start bg-red-100 text-red-800 px-2 py-0.5 rounded-full">
                    Low
                  </span>
                )}
              </div>
              <div className="text-right">
                <p className="text-gray-500">Price</p>
                <p className="font-medium text-gray-800">
                  {currency.symbol}{product.price_per_unit}/{product.unit}
                </p>
              </div>
            </div>

            {isExpanded && (
              <div className="mt-3 space-y-3 text-xs">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-500">Min Quantity</p>
                    <p className="font-medium text-gray-800">{product.min_quantity} {product.unit}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Expiry Date</p>
                    <div className="flex items-center gap-1">
                      <p className={`font-medium ${
                        isExpired ? 'text-red-600' : 
                        isExpiringSoon ? 'text-yellow-600' : 'text-gray-800'
                      }`}>
                        {product.expiry_date ? new Date(product.expiry_date).toLocaleDateString() : 'N/A'}
                      </p>
                      {isWithinAlertPeriod && (
                        <span className={`px-2 py-0.5 rounded-full ${
                          isExpired ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {daysUntilExpiry === 0 ? 'Today' : 
                          daysUntilExpiry < 0 ? 'Expired' : `${daysUntilExpiry}d`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {product.permissible_expiry_days && (
                  <div>
                    <p className="text-gray-500">Alert Before</p>
                    <p className="font-medium text-gray-800">{product.permissible_expiry_days} days</p>
                  </div>
                )}

                {(isLowStock || isExpiringSoon || isExpired) && (
                  <div className={`p-2 rounded-md text-sm ${
                    isExpired ? 'bg-red-50 border border-red-200 text-red-800' :
                    isLowStock ? 'bg-orange-50 border border-orange-200 text-orange-800' :
                    'bg-yellow-50 border border-yellow-200 text-yellow-800'
                  }`}>
                    {isLowStock && (
                      <p>
                        <span className="font-medium">Stock Alert:</span> Only {product.current_quantity} {product.unit} remaining (min: {product.min_quantity})
                      </p>
                    )}
                    {isExpiringSoon && !isExpired && (
                      <p>
                        <span className="font-medium">Expiry Alert:</span> Expires {daysUntilExpiry === 0 ? 'today' : `in ${daysUntilExpiry} days`}
                      </p>
                    )}
                    {isExpired && (
                      <p>
                        <span className="font-medium">Expired:</span> This item expired on {new Date(product.expiry_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductItem;